import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { Box, Chip, Link, Typography } from '@mui/material';

import UsersService from '~/services/UsersService';

const MAX_INITIAL_RESULTS = 10;
const BACKEND_LIMIT = 50;

interface Props {
  user: App.User;
}

export default function SamePhoneAccounts({ user }: Props) {
  const tenant = useSelector((state: App.State) => state.tenant);
  const [showAll, setShowAll] = useState(false);

  const {
    data: accounts = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['similar-accounts', user.phone, tenant.brand],
    queryFn: async ({ signal }) => {
      if (!user.phone) {
        return [];
      }

      const response = await UsersService.getUsers(
        {
          filter: user.phone,
          limit: BACKEND_LIMIT,
          offset: 0,
          brand: tenant.brand,
        },
        { signal },
      );

      // Filter out the current user from the results
      return response.filter((account: App.UserSummary) => account.id_member !== user.id_member);
    },
    enabled: !!user.phone,
  });

  if (!user.phone) {
    return null;
  }

  if (isLoading) {
    return null;
  }

  if (error) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Same Phone Accounts
        </Typography>
        <Typography variant="body2" color="error">
          Error loading similar accounts: {error.message}
        </Typography>
      </Box>
    );
  }

  if (accounts.length === 0) {
    return null;
  }

  const displayedAccounts =
    showAll || accounts.length <= MAX_INITIAL_RESULTS ? accounts : accounts.slice(0, MAX_INITIAL_RESULTS);
  const hasMoreResults = accounts.length > MAX_INITIAL_RESULTS;
  const isAtLimit = accounts.length === BACKEND_LIMIT;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" gutterBottom>
        Similar Accounts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isAtLimit ? 'At least ' : ''}
        {accounts.length} other account{accounts.length !== 1 ? 's' : ''} found with this phone number
        {isAtLimit && ' (we cannot fetch more than 50 at a time, there could be more...)'}:
      </Typography>
      <Box component="ul" sx={{ pl: 2, m: 0 }}>
        {displayedAccounts.map((account) => (
          <Box component="li" key={account.id_member} sx={{ mb: 1 }}>
            <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Link
                href={`/users/${account.id_member}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none' }}
              >
                Account
              </Link>
              {'\u00A0 | \u00A0'}
              <Link
                href={`/purchases/section/allOrders?customer_id=${account.id_member}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none' }}
              >
                Purchases
              </Link>
              {'\u00A0 | \u00A0'}
              <Link
                href={`/users/${account.id_member}/history`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none' }}
              >
                History
              </Link>
              {' \u00A0\u00A0'}
              <strong>{account.full_name}</strong> ({account.email})
              {account.shadow_ban_user && <Chip label="Shadow banned" color="error" size="small" />}
            </Typography>
          </Box>
        ))}
      </Box>
      {hasMoreResults && (
        <Box sx={{ mt: 2 }}>
          <Link component="button" variant="body2" onClick={() => setShowAll(!showAll)} sx={{ cursor: 'pointer' }}>
            {showAll ? 'Hide' : `Show ${accounts.length} similar accounts`}
          </Link>
        </Box>
      )}
    </Box>
  );
}
